import { useAppDispatch } from "@/hooks";
import { sendQuickbooksDataAction } from "@/store/slices/quickbookConnectSlice";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

interface QuickbooksParams {
  code?: string;
  state?: string;
  realmId?: string;
  [key: string]: string | undefined;
}

function QuickbookConnection() {
  const location = useLocation();
  const [params, setParams] = useState<QuickbooksParams>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    try {
      // Parse URL search parameters
      const searchParams = new URLSearchParams(location.search);
      const parsedParams: QuickbooksParams = {};

      // Extract all parameters
      searchParams.forEach((value, key) => {
        parsedParams[key] = value;
      });

      // Common QuickBooks OAuth parameters
      const code = searchParams.get("code") || undefined;
      const state = searchParams.get("state") || undefined;
      const realmId = searchParams.get("realmId") || undefined;

      setParams({
        ...parsedParams,
        code,
        state,
        realmId,
      });

      dispatch(
        sendQuickbooksDataAction({
          code: code || "",
          state: state || "",
          realmId: realmId || "",
        })
      )
        .unwrap()
        .then(() => {
          navigate("/");
        })
        .catch((error) => {
          console.error("Error sending QuickBooks data:", error);
          setError("Failed to send QuickBooks data");
          setIsLoading(false);

          setTimeout(() => {
            navigate("/");
          }, 3000);
        });

      // Here you would typically send these parameters to your backend
      // to exchange the code for access tokens
      console.log("QuickBooks callback parameters:", {
        code,
        state,
        realmId,
        ...parsedParams,
      });

      setIsLoading(false);
    } catch (err) {
      console.error("Error parsing QuickBooks callback parameters:", err);
      setError("Failed to process QuickBooks connection parameters");
      setIsLoading(false);
    }
  }, [location.search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Processing QuickBooks connection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <h2 className="text-xl font-bold">Connection Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">QuickBooks Connection</h1>

      {params.code ? (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Connection Successful!</p>
          <p>Your QuickBooks account has been connected successfully.</p>
        </div>
      ) : (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Connection Incomplete</p>
          <p>No authorization code received from QuickBooks.</p>
        </div>
      )}

      <div className="bg-white shadow-md rounded p-4 mb-4">
        <h2 className="text-xl font-bold mb-2">Connection Parameters</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parameter
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(params).map(([key, value]) => (
                <tr key={key}>
                  <td className="py-2 px-4 border-b border-gray-200">{key}</td>
                  <td className="py-2 px-4 border-b border-gray-200 font-mono">
                    {value || "(empty)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          onClick={() => navigate("/")}
        >
          Return to Dashboard
        </button>
      </div>
    </div>
  );
}

export default QuickbookConnection;
